import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel, {
  type FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import FormGroup, { type FormGroupProps } from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { useId } from 'react';

export interface CheckboxGroupProps<TCheckboxGroupOption> {
  options: TCheckboxGroupOption[];
  getOptionLabel: (option: TCheckboxGroupOption) => string;
  optionIdKey?: TCheckboxGroupOption extends string | number
    ? never
    : keyof TCheckboxGroupOption;
  name?: string;
  value: string[];
  label: string;
  onChange: (value: string[]) => void;
  customRef?: React.Ref<HTMLInputElement>;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  checkboxProps?: CheckboxProps;
  formGroupProps?: Omit<FormGroupProps, 'label' | 'control'>;
  formControlLabelProps?: Omit<FormControlLabelProps, 'label' | 'control'>;
}

export const CheckboxGroup = <
  TCheckboxGroupOption extends string | number | object,
>({
  options,
  getOptionLabel,
  optionIdKey,
  name,
  value = [],
  onChange,
  label,
  customRef,
  onBlur,
  error,
  helperText,
  checkboxProps,
  formGroupProps,
  formControlLabelProps,
}: CheckboxGroupProps<TCheckboxGroupOption>) => {
  const randomName = useId();
  const fieldName = name ?? randomName;

  const toggleValue = (option: string) => {
    if (value.includes(option)) {
      onChange?.(value.filter((v) => v !== option));
    } else {
      onChange?.([...value, option]);
    }
  };

  return (
    <FormControl component="fieldset" variant="standard" error={error}>
      <FormLabel component="legend">{label}</FormLabel>
      <FormGroup ref={customRef} onBlur={onBlur} {...formGroupProps}>
        {options.map((option, index) => {
          const optionValue = optionIdKey
            ? (option[optionIdKey] as string)
            : String(option);

          return (
            <FormControlLabel
              {...formControlLabelProps}
              key={optionValue}
              control={
                <Checkbox
                  checked={value.includes(optionValue)}
                  onChange={() => toggleValue(optionValue)}
                  name={`${fieldName}.${index}`}
                  {...checkboxProps}
                />
              }
              label={getOptionLabel(option)}
            />
          );
        })}
      </FormGroup>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
